/**
 * LE NIN, ÉCRIT SUR UN ACTE ET SUR UN ÉCRAN — une seule rédaction.
 *
 * ⚠️⚠️ LE NUMÉRO COMPLET N'EST JAMAIS IMPRIMÉ, ET C'EST UNE DÉCISION, PAS UNE
 * LIMITE TECHNIQUE. Le registre ne le DÉTIENT pas : `fon.titulaire` porte une
 * empreinte poivrée et les QUATRE DERNIERS chiffres, jamais les quinze — parce
 * qu'un registre foncier national qui stockerait les numéros d'identité de tous
 * ses titulaires serait la cible la plus rentable du pays. Ce que l'acte porte
 * est donc `•••••••••••1234`, assorti de ce qui compte autant : la SOURCE du
 * numéro, et s'il a été VÉRIFIÉ ou seulement déclaré.
 *
 * ⚠️ QUATRE CHIFFRES SUFFISENT À CE QU'UN ACTE DOIT FAIRE : permettre à
 * quiconque tient le papier ET la carte de la personne de constater qu'ils
 * concordent. Ils ne suffisent pas à retrouver un numéro de quinze — c'est
 * exactement le partage voulu.
 *
 * ⚠️ ET « NON RATTACHÉ » S'IMPRIME AUSSI. Une ligne vide se lirait « la donnée
 * n'a pas tenu dans la colonne » ; « aucun NIN rattaché » dit qu'on a regardé.
 * C'est la même règle que partout ici : le silence est ce qu'on refuse.
 */

export interface IdentiteNin {
  /**
   * ⚠️ LE TYPE CHANGE CE QU'ON DOIT DIRE. Une personne MORALE n'a pas de NIN —
   * elle a un RCCM ou un NIF. Imprimer « NIN non rattaché » sous le nom d'une
   * banque est vrai et hors sujet : cela se lit comme une lacune du dossier
   * alors que c'est une catégorie qui ne s'applique pas. Vu sur une copie de
   * titre portant une hypothèque bancaire.
   */
  titulaire_type?: string | null;
  nin_rattache?: boolean | null;
  nin_quatre_derniers?: string | null;
  nin_source?: string | null;
  nin_verifie_le?: string | Date | null;
  mowibo_mid?: string | null;
  identite_verifiee?: boolean | null;
}

const LIBELLE_SOURCE: Record<string, string> = {
  carte_cedeao: 'carte CEDEAO',
  mowibo_id: 'Mowibo ID',
  declaration: 'déclaré par l\'intéressé',
  import_fiscal: 'import fiscal',
};

export const libelleSourceNin = (s: unknown): string =>
  LIBELLE_SOURCE[String(s ?? '')] ?? String(s ?? '—');

/** `•••••••••••1234` — la forme masquée, construite depuis les quatre derniers. */
export function masqueDepuisQuatreDerniers(quatre: string | null | undefined): string | null {
  const q = String(quatre ?? '').trim();
  return /^\d{4}$/.test(q) ? `${'•'.repeat(11)}${q}` : null;
}

/**
 * Une ligne, pour un acte ou un écran. Rend TOUJOURS quelque chose.
 *
 * ⚠️ TROIS ÉTATS, PAS DEUX : vérifié · rattaché mais seulement déclaré · absent.
 * Les deux premiers portent le même masque et n'ont PAS la même valeur en
 * droit — un acte qui les confondrait ferait croire qu'une identité a été
 * constatée alors qu'elle a été rapportée.
 */
export function libelleNin(t: IdentiteNin): string {
  const masque = masqueDepuisQuatreDerniers(t.nin_quatre_derniers);
  const rattache = t.nin_rattache === true || masque !== null;
  const MORALES = new Set(['personne_morale', 'etat', 'collectivite', 'indivision',
                           'communaute_coutumiere']);
  if (!rattache && MORALES.has(String(t.titulaire_type ?? ''))) {
    // Ni lacune ni silence : on nomme l'identifiant qui S'APPLIQUE.
    return t.mowibo_mid
      ? `Personne morale — identifiée par Mowibo ID ${t.mowibo_mid}`
      : 'Personne morale — RCCM ou NIF non renseigné';
  }
  if (!rattache) {
    return t.mowibo_mid ? `NIN non rattaché — identifié par Mowibo ID ${t.mowibo_mid}`
                        : 'NIN non rattaché';
  }
  const tete = masque ?? 'NIN rattaché (quatre derniers non conservés)';
  const source = t.nin_source ? ` · ${libelleSourceNin(t.nin_source)}` : '';
  const etat = t.nin_verifie_le ? ' · VÉRIFIÉ' : ' · non vérifié';
  return `${masque ? 'NIN ' : ''}${tete}${source}${etat}`;
}
